<template>
  <div
    class="position-relative"
    :class="['input-container', { required: isRequired(typeId) }]"
  >
    <template v-if="typeId === formFieldType.text">
      <v-text-field
        v-validate="defaultValidations(typeId)"
        outlined
        v-bind="attrs"
        :placeholder="attrs.placeholder || `اكتب ${attrs.label}`"
        @input="onInput"
        @blur="onBlur"
      ></v-text-field>
    </template>

    <template v-if="typeId === formFieldType.number">
      <v-text-field
        v-validate="defaultValidations(typeId)"
        v-mask="numberMasking"
        outlined
        v-bind="attrs"
        :placeholder="attrs.placeholder || `اكتب ${attrs.label}`"
        @input="onNumberInput"
      ></v-text-field>
    </template>

    <template v-if="typeId === formFieldType.textarea || typeId === formFieldType.formula">
      <v-textarea
        v-validate="defaultValidations(typeId)"
        outlined
        v-bind="attrs"
        :counter="textareaCounter"
        :placeholder="attrs.placeholder || `اكتب ${attrs.label}`"
        auto-grow
        @input="onInput"
      ></v-textarea>
    </template>

    <template v-if="typeId === formFieldType.date">
      <div class="datepickers">
        <div
          v-if="!hideHijriDate"
          class="datepickers-item"
        >
          <naj-datepicker
            locale="ar-SA"
            v-bind="attrs"
            :value="datepickerValue"
            :label="`${attrs.label} هجري`"
            :placeholder="attrs.placeholder || `ادخل ${attrs.label}`"
            :max="$dateFormat(maxDate)"
            :min="$dateFormat(minDate)"
            @change="onChange"
          ></naj-datepicker>
        </div>

        <div class="datepickers-item">
          <naj-datepicker
            v-bind="attrs"
            :value="datepickerValue"
            :label="`${attrs.label} ميلادي`"
            :placeholder="attrs.placeholder || `ادخل ${attrs.label}`"
            :max="$dateFormat(maxDate)"
            :min="$dateFormat(minDate)"
            @change="onChange"
          ></naj-datepicker>
        </div>
      </div>
    </template>

    <template v-if="typeId === formFieldType.selectbox || typeId === formFieldType.multipleSelectBox">
      <label
        v-if="selectedItemsLength === 0"
        class="label-placeholder"
      >{{ attrs.placeholder || `اختر ${attrs.label}` }}</label>

      <v-autocomplete
        v-validate="defaultValidations(typeId)"
        outlined
        :no-data-text="local.common.selectBoxNoData"
        v-bind="attrs"
        :multiple="typeId === formFieldType.multipleSelectBox"
        :return-object="true"
        autocomplete="off"
        :class="{'more-than-one': selectedItemsLength > 1}"
        :filter="filterSelectboxItems"
        @change="onSelectionChange"
      >
        <template
          v-if="typeId === formFieldType.multipleSelectBox"
          v-slot:prepend-item
        >
          <v-list-item
            ripple
            @click="selectAllToggle"
          >
            <v-list-item-action>
              <v-icon class="primary--text">
                {{ selectAllIcon }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ local.common.all }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-2"></v-divider>
        </template>
        <template
          v-if="typeId === formFieldType.multipleSelectBox"
          v-slot:selection="{ item, index }"
        >
          <span
            v-if="index === 0"
            class="multi-selection"
          >{{ item[attrs['item-text']] }}</span>
          <span
            v-if="index === 1"
            class="multi-selection-caption"
          >{{ local.common.selectedOptions(selectedItemsLength - 1) | hindiNumbers }}</span>
        </template>
        <template v-slot:item="slotItem">
          <v-tooltip
            :disabled="!slotItem.item[attrs['item-tooltip']]"
            bottom
          >
            <template #activator="activator">
              <v-layout
                v-bind="activator.attrs"
                v-on="activator.on"
              >
                <v-list-item-action v-if="typeId === formFieldType.multipleSelectBox">
                  <v-checkbox
                    :input-value="slotItem.attrs.inputValue"
                    :ripple="false"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ slotItem.item[attrs['item-text']] || slotItem.item }}</v-list-item-title>
                  <v-list-item-subtitle v-if="slotItem.item[attrs['item-description']]">
                    {{ slotItem.item[attrs['item-description']] }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-icon v-if="!!slotItem.item[attrs['item-icon']]">
                  <v-icon v-text="slotItem.item[attrs['item-icon']]"></v-icon>
                </v-list-item-icon>
                <v-list-item-icon v-else-if="!!slotItem.item[attrs['item-tooltip']] && !slotItem.item[attrs['item-icon']]">
                  <v-icon>mdi-alert-circle</v-icon>
                </v-list-item-icon>
              </v-layout>
            </template>
            <pre v-text="slotItem.item[attrs['item-tooltip']]" />
          </v-tooltip>
        </template>
      </v-autocomplete>
    </template>

    <template v-if="typeId === formFieldType.checkbox">
      <v-checkbox
        v-validate="defaultValidations(typeId)"
        v-bind="attrs"
        :input-value="attrs.value"
        :true-value="true"
        :false-value="false"
        @change="onChange"
      ></v-checkbox>
    </template>

    <template v-if="typeId === formFieldType.radioButton">
      <v-radio-group
        v-validate="defaultValidations(typeId)"
        v-bind="attrs"
        @blur="$emit('blur', $event)"
        @change="onRadioChange"
      >
        <div class="options-list">
          <div
            v-for="radio in attrs.items"
            :key="radio[attrs['item-value']]"
            class="radio-button-option"
          >
            <v-radio
              v-bind="attrs"
              :label="radio[attrs['item-text']]"
              :value="radio[attrs['item-value']]"
            ></v-radio>
          </div>
        </div>
      </v-radio-group>
    </template>

    <v-tooltip
      v-if="tooltipContent"
      top
    >
      <template v-slot:activator="{ on }">
        <div class="tooltip-button">
          <v-icon
            class="flip-horizontal"
            v-on="on"
          >
            mdi-help-circle-outline
          </v-icon>
        </div>
      </template>

      <div v-html="tooltipContent" />
    </v-tooltip>
  </div>
</template>

<script>
import { generateHtmlTagId } from '../../helpers/utils';
import NajDatepicker from './naj-datepicker.vue';

export default {
  name: 'NajInput',
  inject: ['$validator'],
  components: { NajDatepicker },
  props: {
    typeId: {
      required: true,
      validator(value) {
        return !isNaN(value);
      },
    },
    locale: {
      type: String,
      default: 'en-US',
    },
    disableMaxDate: {
      type: Boolean,
      default: false,
    },
    hideHijriDate: {
      type: Boolean,
      default: false,
    },
    maxDate: {
      type: Date,
      default: function () {
        return this.disableMaxDate ? null : new Date();
      },
    },
    minDate: { type: Date },
    tooltipContent: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      datepickerMenu: false,
      generatedId: null,
      defaultValidations: typeId =>
      ({
        [this.formFieldType.text]: this.computedValidates('string'),
        [this.formFieldType.number]: this.computedValidates('numeric|int'),
        [this.formFieldType.textarea]: this.computedValidates(`max:${this.textareaCounter}`),
        [this.formFieldType.formula]: this.computedValidates(`max:${this.textareaCounter}`),
        [this.formFieldType.date]: this.computedValidates(),
        [this.formFieldType.selectbox]: this.computedValidates(),
        [this.formFieldType.checkbox]: this.computedValidates(),
        [this.formFieldType.radioButton]: this.computedValidates(),
        [this.formFieldType.multipleSelectBox]: this.computedValidates(),
      }[typeId]),
    };
  },
  computed: {
    validates() {
      const attrsValidates = this.$attrs.validates;
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.$attrs.validates = null;
      if (attrsValidates && typeof attrsValidates === 'object') {
        const validates = [];
        for (const key in attrsValidates) {
          if (Object.prototype.hasOwnProperty.call(attrsValidates, key)) {
            validates.push(`${key}:${attrsValidates[key]}`);
          }
        }
        return validates.join('|');
      } else {
        return attrsValidates;
      }
    },
    attrs() {
      const { id, ...attrs } = this.$attrs;
      attrs.id = this.generatedId;
      attrs['data-vv-name'] = this.generatedId;
      attrs['data-vv-as'] = attrs.label ? attrs.label.replace(':', '')
        .trim() : ' ';
      attrs['error-messages'] = this.errors.first(attrs.id);
      if (attrs.readonly) {
        attrs.readonly = true;
        attrs.clearable = false;
        attrs.validates = undefined;
        attrs.disabled = undefined;
      } else {
        attrs.clearable = attrs.clearable == undefined ? true : attrs.clearable;
        attrs.validates = this.validates;
      }
      return attrs;
    },
    numberMasking() {
      const validates = this.defaultValidations(this.formFieldType.number);
      const validations = validates.split('|')
        .filter(str => !str.includes(':true'));

      const digitsValidation = validations.find(v => v.includes('digits') || v.includes('max'));
      if (digitsValidation) {
        const numberOfHashes = digitsValidation.split(':')[1];
        return '#'.repeat(numberOfHashes);
      }

      if (validations.some(str => str.includes('hijri_year'))) return '####';
      if (validations.some(str => str.includes('long'))) return '###################';

      return '##########'; // default int 10 digits
    },
    datepickerValue() {
      const indexOfT = this.$attrs.value ? this.$attrs.value.indexOf('T') : -1;
      return this.$attrs.value && indexOfT != -1
        ? this.$attrs.value.slice(0, indexOfT)
        : this.$attrs.value;
    },
    textareaCounter() {
      const defaultCounter = this.$attrs.counter || (this.typeId === this.formFieldType.formula ? 1000000 : 1000);
      const validates = this.validates ? this.validates + '|' : '';
      const counter = (validates + `max:${defaultCounter}`).match(/max:([0-9]+)/)[1];

      return +counter;
    },
    itemsLength() {
      return ((this.attrs || {}).items || []).length;
    },
    selectedItemsLength() {
      return ((this.attrs || {}).value || []).length;
    },
    selectAllIcon() {
      if (this.itemsLength == this.selectedItemsLength) return 'mdi-close-box';
      if (this.selectedItemsLength > 0 && this.itemsLength !== this.selectedItemsLength) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
  },
  watch: {
    validates(newVal) {
      this.$nextTick(() => {
        if (newVal && (newVal.includes('isValid') || newVal.includes('isVerified'))) {
          this.$validator.validate(this.attrs.id);
        }
      });
    },
  },
  beforeMount() {
    this.generatedId = generateHtmlTagId(this, 'input');
  },
  mounted() {
    this.$emit('update:generated-id', this.generatedId);
    this.$nextTick(() => {
      this.$emit('mounted', this.attrs.value);
    });
  },
  methods: {
    updateValue(value) {
      this.$emit('update:value', value);
    },
    onInput(value) {
      if (value && typeof (value) === 'string') value = value.trim();
      this.updateValue(value);
      this.$emit('input', value);
    },
    onNumberInput(value) {
      this.onInput(!value ? value : +value);
    },
    onChange(value) {
      this.updateValue(value);
      this.$emit('change', value);
    },
    onBlur(value) {
      if (value && typeof (value) === 'string') value = value.trim();
      this.$emit('blur', value);
    },
    onSelectionChange(value) {
      if (Array.isArray(value)) {
        // eslint-disable-next-line no-param-reassign
        value = value
          .map(item =>
            typeof item === 'object'
              ? item
              : this.attrs.items.find(i => i[this.attrs['item-value']] == item)
              || {
                [this.attrs['item-value']]: item,
                [this.attrs['item-text']]: item,
              }
          )
          .sort((a, b) => a[this.attrs['item-value']] - b[this.attrs['item-value']]);
      }
      const getMultiselectArray = (arr, valueKey) => arr.map(item => item[valueKey]);
      const returnValue = value
        ? typeof value === 'string' || typeof value === 'number'
          ? value
          : Array.isArray(value)
            ? getMultiselectArray(value, this.attrs['item-value'])
            : value[this.attrs['item-value']]
        : undefined;
      const returnText = value
        ? typeof value === 'string'
          ? value
          : Array.isArray(value)
            ? getMultiselectArray(value, this.attrs['item-text'])
            : value[this.attrs['item-text']]
        : undefined;
      this.onChange(returnValue);
      this.$emit('update:valueName', returnText);
    },
    onRadioChange(value) {
      const selectedValue = this.attrs.items.find(i => i[this.attrs['item-value']] === value);
      this.onChange(value);
      this.$emit('update:valueName', (selectedValue || {})[this.attrs['item-text']]);
    },
    onKeydown($event) {
      this.$emit('keydown', $event);
    },
    isNumbersOnlyAllowed($event) {
      const charCode = $event.which || $event.keyCode;
      if (
        (!$event.shiftKey && charCode <= 57) ||
        (!$event.shiftKey && charCode >= 96 && charCode <= 105) ||
        $event.ctrlKey
      ) {
        return true;
      }
      return false;
    },
    onKeyup(e) {
      this.$emit('keyup', e);
    },
    computedValidates(defaultValidations) {
      const validates = this.validates;
      if (this.attrs.readonly) return '';
      if (!defaultValidations) return validates || '';
      if (!validates) return defaultValidations || '';

      const validationsArr = validates.split('|');
      let defaultValidationsArr = defaultValidations.split('|');

      defaultValidationsArr = this.excludeDefaultValidations(defaultValidationsArr);

      const allValidations = [];

      validationsArr.forEach((v1) => {
        const v1Key = v1.split(':');
        const index = allValidations.findIndex((v2) => {
          const v2Key = v2.split(':');
          return v1Key[0] === v2Key[0];
        });
        if (index === -1) allValidations.push(v1);
      });

      defaultValidationsArr.forEach((v1) => {
        const v1Key = v1.split(':');
        const index = allValidations.findIndex((v2) => {
          const v2Key = v2.split(':');
          return v1Key[0] === v2Key[0];
        });
        if (index === -1) allValidations.push(v1);
      });

      this.sortValidations(allValidations);

      return allValidations.join('|');
    },
    excludeDefaultValidations(validations) {
      const validates = this.validates.split('|');
      const excludeConfig = {
        maxLength: ['string'],
        max: ['long', 'int', 'string'],
        long: ['int'],
      };

      return validations.filter(
        v1 =>
          !validates.some(v2 => (excludeConfig[v2.split(':')[0]] || []).some(v3 => v1.includes(v3)))
      );
    },
    sortValidations(validations) {
      const validationsMustSorted = [
        'required',
        'isValid',
        'conditionalRequired',
        'numeric',
        'max',
        'long',
        'int',
      ];

      validationsMustSorted.reverse()
        .forEach((str) => {
          const index = validations.findIndex(v => v.includes(str));
          if (index !== -1) {
            const validation = validations[index];
            validations.splice(index, 1);
            validations.unshift(validation);
          }
        });
    },
    isRequired(typeId) {
      const validations = this.defaultValidations(typeId) || '';
      return validations.includes('required') || validations.includes('conditionalRequired:true');
    },
    selectAllToggle() {
      this.onSelectionChange(this.selectedItemsLength === this.itemsLength
        ? []
        : this.attrs.items.slice());
    },
    filterSelectboxItems(item, queryText) {
      return (item).toString()
        .toLowerCase() === queryText.toLowerCase()
        || (item[this.attrs['item-value']] || '').toString()
          .toLowerCase() === queryText.toLowerCase()
        || (item[this.attrs['item-text']] || '').toString()
          .toLowerCase()
          .includes(queryText.toLowerCase())
        || (item[this.attrs['item-description']] || '').toString()
          .toLowerCase()
          .includes(queryText.toLowerCase())
        || (item[this.attrs['item-tooltip']] || '').toString()
          .toLowerCase()
          .includes(queryText.toLowerCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.position-relative {
  position: relative;
}
.label-placeholder {
  position: absolute;
  top: 30px;
  z-index: 1;
  color: #c7c7c7;
  right: 5px;
}
.required ::v-deep {
  *:not(.v-radio) > .v-label:after {
    content: "*";
    color: #ff3547;
    display: inline-block;
    margin-right: 5px;
  }
}
::v-deep .v-input--radio-group {
  &__input {
    display: block;
    text-align: right;
  }
  .v-input__control {
    width: 100%;
  }
  .v-radio {
    text-align: right;
  }
  .v-input--radio-group__input {
    > .v-label {
      font-size: 19px;
      text-align: right;
    }
  }
  .options-list {
    display: flex;
  }
  .radio-button-option {
    display: inline-flex;
    align-items: center;
    padding: 10px 10px 10px 30px;
  }
}

::v-deep .v-input {
  $input-color: rgba(0, 0, 0, 0.54);
  .primary--text {
    color: #203f39 !important;
    caret-color: #203f39 !important;
  }

  .v-input--selection-controls__input
    .v-input--selection-controls__ripple:before {
    background-color: transparent !important;
  }
  &.v-text-field--outlined {
    color: $input-color;
    border-radius: 6px;
    font-size: 20px;
    margin-top: 26px;
    background-color: transparent;
    z-index: 5;
    fieldset {
      border-width: 2px;
      display: inline-flex;
      background-color: #ffffff;
      legend {
        width: 0 !important;
      }
    }
    &.v-input--is-disabled {
      fieldset {
        background-color: #ddd;
      }
    }
    &.v-input--is-readonly {
      fieldset {
        background-color: #eee;
      }
      .v-input__append-inner {
        display: none;
      }
    }
    &.error--text:not(.v-input--is-focused) {
      fieldset {
        border-color: rgba(0, 0, 0, 0.24);
      }
    }
    .v-input__slot {
      min-height: 35px;
    }
    input,
    textarea {
      color: #000;
      max-height: none;
      padding: 8px 5px 8px 0;
    }
    ::placeholder,
    :-ms-input-placeholder {
      color: rgb(192, 192, 192);
    }
    .v-label--active {
      -webkit-transform: translateY(-24px) scale(0.75);
      transform: translateY(-24px) scale(0.75);
    }
    .v-label {
      height: 25px;
      font-size: 25px;
      line-height: 25px;
      font-weight: 500;
      right: -12px !important; // 30-10-2019 needs test in many cases
      color: #000;
      overflow: visible;
      -webkit-transform: translateY(-45px) scale(0.75);
      transform: translateY(-45px) scale(0.75);
    }
    &.more-than-one .multi-selection {
      max-width: 70%;
    }
    &.v-select--is-multi.v-input--is-focused {
      .multi-selection,
      .multi-selection-caption {
        display: none;
      }
    }
    .multi-selection {
      color: #000;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .multi-selection-caption {
      font-size: 14px;
    }
  }
  .v-input__icon {
    z-index: 0;
    &.v-input__icon--clear {
      -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      .v-icon {
        color: $input-color;
      }
    }
  }
  &:not(:hover) .v-input__icon--clear {
    opacity: 0;
  }
  .v-text-field__details {
    margin-bottom: 0;
  }
  &.v-text-field--enclosed {
    .v-input__prepend-outer,
    .v-input__prepend-inner,
    .v-input__append-inner,
    .v-input__append-outer {
      margin-top: 0;
      height: 36px;
      align-items: center;
    }
  }
  &.v-input--selection-controls {
    &.v-input--radio-group {
      margin-top: 0;
      padding-top: 0;
    }
    .v-label {
      color: #000;
      font-weight: 500;
      .primary-label-color & {
        color: #009587;
      }
    }
  }
  &.v-input--checkbox {
    margin-top: 0;
    padding-top: 0;
    .v-messages {
      display: none;
    }
  }
}
.datepickers {
  display: flex;
  width: 100%;
  .datepickers-item {
    flex: 1;
    &:first-child {
      margin-left: 10px;
    }
    &:last-child {
      margin-right: 10px;
    }
  }
}
::v-deep .v-list-item__action:first-child {
  margin-left: 5px;
}
::v-deep .v-list-item__action:first-child,
::v-deep .v-list-item__icon:first-child {
  margin-right: 0;
}
.input-container {
  display: flex;
  width: 100%;
}
.tooltip-button {
  display: flex;
  align-items: center;
  align-self: center;
  padding-right: 10px;
}

::v-deep {
  .v-input {
    &.v-text-field--outlined {
      .v-label {
        font-size: 17px !important;
        color: #34675a !important;
      }

      input,
      textarea {
        color: #34675a;
        max-height: none;
        padding: 0;
        font-size: 16px;
        font-weight: bold;
      }

      .v-input__slot {
        min-height: 35px;
      }
    }

    &.v-text-field--outlined fieldset {
      padding: 14px 7px;
      border: none;
      font-weight: bold;
      height: auto;
      min-height: 1px;
      border-radius: 0;
      box-shadow: 0px 0px 2px #6c757d;
      background-color: transparent;
    }

    &.v-text-field--outlined fieldset {
      padding: 5px;
      background-color: white;
      border-bottom: 1px solid #b69a70;
      font-weight: bold;
      height: auto;
      min-height: 1px;
      border-left: none;
      border-right: none;
      border-top: none;
      border-radius: 0;
    }
    &.v-text-field--outlined.v-input--is-disabled fieldset {
      background-color: #f7f7f7;
      padding: 5px;
      border-bottom: 1px solid #b69a70;
      font-weight: bold;
      height: auto;
      min-height: 1px;
      border-left: none;
      border-right: none;
      border-top: none;
      border-radius: 0;
    }
  }
}
.view-input {
  ::v-deep {
    .v-input {
      &.v-text-field--outlined {
        .v-label {
          font-size: 20pxs !important;
          font-weight: 500 !important;
          color: #34675a !important;
        }

        input,
        textarea {
          color: #34675a;
          max-height: none;
          padding: 0;
          font-size: 16px;
        }

        .v-input__slot {
          min-height: 36px;
        }
      }

      &.v-text-field--outlined.v-input--is-disabled fieldset {
        padding: 5px;
        background-color: #f7f7f7;
        border-bottom: 1px solid #b69a70;
        font-weight: bold;
        height: auto;
        min-height: 1px;
        border-left: none;
        border-right: none;
        border-top: none;
        border-radius: 0;
      }
    }
  }
}
</style>
